import { ChevronDown, Loader2, Plus, Trash2 } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MarkdownView } from "@/components/shared/MarkdownView";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CERTIFICATE_FULFILLMENT_MODE_SLUGS } from "@/constants/certificateFulfillmentModes";
import { cn } from "@/lib/utils";
import type {
  CertificateProductFormMode,
  CertificateProductFormValues,
} from "@/pages/platform/certificate-product-form-types";
import { formatCertificatePricingProduct } from "@/utils/certificatePricingDisplay";

const CUSTOM_SLUG_SELECT_VALUE = "__custom_slug__";

export type CertificateProductFormFieldsProps = {
  mode: CertificateProductFormMode;
  suggestedTargetTypeSlugs: string[];
  documentOptionsLoading: boolean;
  documentOptionSlugs: string[];
  isSaving: boolean;
  form: CertificateProductFormValues;
  setForm: Dispatch<SetStateAction<CertificateProductFormValues>>;
  onAddTargetTypeChange: (value: string) => void;
  toggleRequiredDoc: (slug: string, checked: boolean) => void;
  toggleFulfillmentMode: (slug: string, checked: boolean) => void;
};

export function CertificateProductFormFields({
  mode,
  suggestedTargetTypeSlugs,
  documentOptionsLoading,
  documentOptionSlugs,
  isSaving,
  form,
  setForm,
  onAddTargetTypeChange,
  toggleRequiredDoc,
  toggleFulfillmentMode,
}: CertificateProductFormFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const [customSlugMode, setCustomSlugMode] = useState(false);

  const presetSlugSet = useMemo(
    () => new Set(suggestedTargetTypeSlugs),
    [suggestedTargetTypeSlugs],
  );

  useEffect(() => {
    if (mode === "add") {
      setCustomSlugMode(false);
    }
  }, [mode]);

  const slugInPreset = form.targetType !== "" && presetSlugSet.has(form.targetType);
  const slugSelectValue = slugInPreset
    ? form.targetType
    : customSlugMode
      ? CUSTOM_SLUG_SELECT_VALUE
      : undefined;

  const showCustomSlugInput =
    mode === "add" &&
    (customSlugMode || (form.targetType !== "" && !presetSlugSet.has(form.targetType)));

  const handleSlugSelectChange = (value: string): void => {
    if (value === CUSTOM_SLUG_SELECT_VALUE) {
      setCustomSlugMode(true);
      onAddTargetTypeChange("");
      return;
    }
    setCustomSlugMode(false);
    onAddTargetTypeChange(value);
  };

  return (
    <div className="space-y-4 py-2">
      {mode === "add" ? (
        <div className="space-y-2">
          <Label htmlFor="cert-add-slug-select">{t("platform.products.productSlugLabel")}</Label>
          {suggestedTargetTypeSlugs.length > 0 ? (
            <Select
              value={slugSelectValue}
              onValueChange={handleSlugSelectChange}
              disabled={isSaving}
            >
              <SelectTrigger
                id="cert-add-slug-select"
                className="transition-colors duration-150"
              >
                <SelectValue placeholder={t("platform.products.selectTargetTypePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {suggestedTargetTypeSlugs.map((slug) => (
                  <SelectItem key={slug} value={slug} className="cursor-pointer">
                    <span className="flex flex-col items-start gap-0.5 py-0.5 text-left">
                      <span className="font-medium leading-tight text-foreground">
                        {formatCertificatePricingProduct(slug, t)}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{slug}</span>
                    </span>
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_SLUG_SELECT_VALUE} className="cursor-pointer">
                  {t("platform.products.productSlugCustom")}
                </SelectItem>
              </SelectContent>
            </Select>
          ) : null}
          {suggestedTargetTypeSlugs.length === 0 || showCustomSlugInput ? (
            <Input
              id="cert-add-slug"
              value={form.targetType}
              onChange={(e) => onAddTargetTypeChange(e.target.value)}
              disabled={isSaving}
              autoComplete="off"
              placeholder={t("platform.products.productSlugPlaceholder")}
              className="font-mono text-sm transition-colors duration-150"
            />
          ) : null}
          <p className="text-xs text-muted-foreground">{t("platform.products.productSlugHint")}</p>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="cert-display-name">
          {t("platform.products.displayNameLabel", "Nombre visible")}
        </Label>
        <Input
          id="cert-display-name"
          value={form.displayName}
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          disabled={isSaving}
          autoComplete="off"
          maxLength={128}
          placeholder={t(
            "platform.products.displayNamePlaceholder",
            "Ej: Certificación de firma",
          )}
          className="transition-colors duration-150"
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "platform.products.displayNameHint",
            "Etiqueta mostrada a clientes y profesionales en portal y apps móviles.",
          )}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <Checkbox
            id="cert-ch-acc"
            checked={form.involvesAccountant}
            onCheckedChange={(c) => {
              const checked = c === true;
              setForm((f) => ({
                ...f,
                involvesAccountant: checked,
                ...(checked ? { urType: "arancel" as const } : {}),
              }));
            }}
            disabled={isSaving}
            className="transition-colors duration-150"
          />
          <Label htmlFor="cert-ch-acc" className="cursor-pointer font-normal">
            {t("platform.products.channelAccountant")}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="cert-ch-not"
            checked={form.involvesNotary}
            onCheckedChange={(c) => setForm((f) => ({ ...f, involvesNotary: c === true }))}
            disabled={isSaving}
            className="transition-colors duration-150"
          />
          <Label htmlFor="cert-ch-not" className="cursor-pointer font-normal">
            {t("platform.products.channelNotary")}
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("platform.products.fulfillmentModesLabel")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.fulfillmentModesHint")}</p>
        <div className="space-y-2 rounded-md border border-border p-3">
          {CERTIFICATE_FULFILLMENT_MODE_SLUGS.map((slug) => (
            <div key={slug} className="flex items-center gap-2">
              <Checkbox
                id={`fulfillment-${slug}`}
                checked={form.fulfillmentModes.includes(slug)}
                onCheckedChange={(c) => toggleFulfillmentMode(slug, c === true)}
                disabled={isSaving}
                className="transition-colors duration-150"
              />
              <Label
                htmlFor={`fulfillment-${slug}`}
                className="cursor-pointer font-normal leading-snug text-foreground"
              >
                {t(`platform.products.fulfillmentModeLabels.${slug}`)}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>{t("platform.products.fixedCostsLabel")}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            disabled={isSaving}
            onClick={() =>
              setForm((f) => ({
                ...f,
                fixedCostItems: [
                  ...f.fixedCostItems,
                  { label: "", pesos: "0.00", includesIva: true },
                ],
              }))
            }
          >
            <Plus className="h-3.5 w-3.5" />
            {t("platform.products.fixedCostAdd")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("platform.products.fixedCostsHint")}</p>
        <div className="space-y-3">
          {form.fixedCostItems.map((item, index) => (
            <div
              key={index}
              className="rounded-md border border-border p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <Input
                    placeholder={t("platform.products.fixedCostLabelPlaceholder")}
                    value={item.label}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        fixedCostItems: f.fixedCostItems.map((it, i) =>
                          i === index ? { ...it, label: e.target.value } : it,
                        ),
                      }))
                    }
                    disabled={isSaving}
                    className="text-sm transition-colors duration-150"
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      inputMode="decimal"
                      value={item.pesos}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          fixedCostItems: f.fixedCostItems.map((it, i) =>
                            i === index ? { ...it, pesos: e.target.value } : it,
                          ),
                        }))
                      }
                      disabled={isSaving}
                      className="transition-colors duration-150"
                    />
                    <span className="shrink-0 text-sm text-muted-foreground">UYU</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`cert-iva-fixed-${index}`}
                      checked={item.includesIva}
                      onCheckedChange={(c) =>
                        setForm((f) => ({
                          ...f,
                          fixedCostItems: f.fixedCostItems.map((it, i) =>
                            i === index ? { ...it, includesIva: c === true } : it,
                          ),
                        }))
                      }
                      disabled={isSaving}
                      className="transition-colors duration-150"
                    />
                    <Label
                      htmlFor={`cert-iva-fixed-${index}`}
                      className="cursor-pointer text-sm font-normal"
                    >
                      {t("platform.products.ivaAmountIncludesLabel")}
                    </Label>
                  </div>
                </div>
                {form.fixedCostItems.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={isSaving}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        fixedCostItems: f.fixedCostItems.filter((_, i) => i !== index),
                      }))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-price-ur">{t("platform.products.colUrUnits")}</Label>
        <Input
          id="cert-price-ur"
          inputMode="decimal"
          value={form.urUnits}
          onChange={(e) => setForm((f) => ({ ...f, urUnits: e.target.value }))}
          disabled={isSaving}
          className="transition-colors duration-150"
        />
        <div className="space-y-1">
          <Label htmlFor="cert-ur-type">{t("platform.products.urTypeLabel")}</Label>
          <Select
            value={form.urType}
            onValueChange={(v) => setForm((f) => ({ ...f, urType: v as "dgi" | "arancel" }))}
            disabled={isSaving}
          >
            <SelectTrigger id="cert-ur-type" className="transition-colors duration-150">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dgi">{t("platform.products.urTypeDgi")}</SelectItem>
              <SelectItem value="arancel">{t("platform.products.urTypeArancel")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t("platform.products.urTypeHint")}</p>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="cert-iva-ur"
            checked={form.urVariableIncludesIva}
            onCheckedChange={(c) =>
              setForm((f) => ({ ...f, urVariableIncludesIva: c === true }))
            }
            disabled={isSaving}
            className="mt-0.5 transition-colors duration-150"
          />
          <div className="space-y-0.5">
            <Label htmlFor="cert-iva-ur" className="cursor-pointer font-normal leading-snug">
              {t("platform.products.ivaAmountIncludesLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">{t("platform.products.ivaUrPortionHint")}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-margin">{t("platform.products.colMargin")}</Label>
        <Input
          id="cert-margin"
          inputMode="decimal"
          value={form.marginPercent}
          onChange={(e) => setForm((f) => ({ ...f, marginPercent: e.target.value }))}
          disabled={isSaving}
          className="transition-colors duration-150"
        />
        <div className="flex items-start gap-2">
          <Checkbox
            id="cert-iva-margin"
            checked={form.marginIncludesIva}
            onCheckedChange={(c) => setForm((f) => ({ ...f, marginIncludesIva: c === true }))}
            disabled={isSaving}
            className="mt-0.5 transition-colors duration-150"
          />
          <div className="space-y-0.5">
            <Label htmlFor="cert-iva-margin" className="cursor-pointer font-normal leading-snug">
              {t("platform.products.ivaAmountIncludesLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">{t("platform.products.ivaMarginPortionHint")}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-per-file">{t("platform.products.perFileStorageLabel")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.perFileStorageHint")}</p>
        <div className="flex items-center gap-2">
          <Input
            id="cert-per-file"
            inputMode="decimal"
            value={form.perFileStoragePesos}
            onChange={(e) => setForm((f) => ({ ...f, perFileStoragePesos: e.target.value }))}
            disabled={isSaving}
            className="transition-colors duration-150"
          />
          <span className="shrink-0 text-sm text-muted-foreground">UYU</span>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="cert-iva-per-file"
            checked={form.perFileStorageIncludesIva}
            onCheckedChange={(c) =>
              setForm((f) => ({ ...f, perFileStorageIncludesIva: c === true }))
            }
            disabled={isSaving}
            className="mt-0.5 transition-colors duration-150"
          />
          <div className="space-y-0.5">
            <Label htmlFor="cert-iva-per-file" className="cursor-pointer font-normal leading-snug">
              {t("platform.products.ivaAmountIncludesLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">{t("platform.products.ivaAmountIncludesHint")}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-delivery-fee">{t("platform.products.deliveryFeeLabel")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.deliveryFeeHint")}</p>
        <div className="flex items-center gap-2">
          <Input
            id="cert-delivery-fee"
            inputMode="decimal"
            value={form.deliveryFeePesos}
            onChange={(e) => setForm((f) => ({ ...f, deliveryFeePesos: e.target.value }))}
            disabled={isSaving}
            className="transition-colors duration-150"
          />
          <span className="shrink-0 text-sm text-muted-foreground">UYU</span>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="cert-iva-delivery"
            checked={form.deliveryFeeIncludesIva}
            onCheckedChange={(c) =>
              setForm((f) => ({ ...f, deliveryFeeIncludesIva: c === true }))
            }
            disabled={isSaving}
            className="mt-0.5 transition-colors duration-150"
          />
          <div className="space-y-0.5">
            <Label htmlFor="cert-iva-delivery" className="cursor-pointer font-normal leading-snug">
              {t("platform.products.ivaAmountIncludesLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">{t("platform.products.ivaAmountIncludesHint")}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-stamp">{t("platform.products.professionalStampLabel")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.professionalStampHint")}</p>
        <div className="flex items-center gap-2">
          <Input
            id="cert-stamp"
            inputMode="decimal"
            value={form.professionalStampPesos}
            onChange={(e) => setForm((f) => ({ ...f, professionalStampPesos: e.target.value }))}
            disabled={isSaving}
            className="transition-colors duration-150"
          />
          <span className="shrink-0 text-sm text-muted-foreground">UYU</span>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-service-fee">
          {t("platform.products.consumerServiceFeeLabel")}
        </Label>
        <p className="text-xs text-muted-foreground">
          {t("platform.products.consumerServiceFeeHint")}
        </p>
        <div className="flex items-center gap-2">
          <Input
            id="cert-service-fee"
            inputMode="decimal"
            value={form.consumerServiceFeePesos}
            onChange={(e) =>
              setForm((f) => ({ ...f, consumerServiceFeePesos: e.target.value }))
            }
            disabled={isSaving}
            className="transition-colors duration-150"
          />
          <span className="shrink-0 text-sm text-muted-foreground">UYU</span>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-currency">{t("platform.products.currency")}</Label>
        <Input
          id="cert-currency"
          value={form.currency}
          onChange={(e) =>
            setForm((f) => ({ ...f, currency: e.target.value.toUpperCase().slice(0, 8) }))
          }
          disabled={isSaving}
          maxLength={8}
          className="transition-colors duration-150"
        />
      </div>

      <div className="space-y-2">
        <Label>{t("platform.products.requiredDocuments")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.requiredDocumentsHint")}</p>
        <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-border p-3">
          {documentOptionsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
              {t("platform.products.loadingDocOptions")}
            </div>
          ) : (
            documentOptionSlugs.map((slug) => (
              <div key={slug} className="flex items-center gap-2">
                <Checkbox
                  id={`doc-${slug}`}
                  checked={form.requiredDocs.includes(slug)}
                  onCheckedChange={(c) => toggleRequiredDoc(slug, c === true)}
                  disabled={isSaving}
                  className="transition-colors duration-150"
                />
                <Label htmlFor={`doc-${slug}`} className="cursor-pointer font-mono text-xs font-normal">
                  {slug}
                </Label>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cert-price-description">{t("platform.products.descriptionLabel")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.descriptionMarkdownHint")}</p>
        <Textarea
          id="cert-price-description"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          disabled={isSaving}
          placeholder={t("platform.products.descriptionPlaceholder")}
          rows={5}
          className="min-h-[120px] resize-y transition-colors duration-150"
        />
        <Collapsible
          open={form.descriptionPreviewOpen}
          onOpenChange={(next) => setForm((f) => ({ ...f, descriptionPreviewOpen: next }))}
        >
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-muted-foreground transition-colors duration-150"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  form.descriptionPreviewOpen && "rotate-180",
                )}
              />
              {t("platform.products.descriptionPreview")}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="rounded-md border border-border bg-muted/30 p-3">
            {form.description.trim() ? (
              <MarkdownView markdown={form.description.trim()} />
            ) : (
              <p className="text-sm text-muted-foreground">{t("platform.products.descriptionEmpty")}</p>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-business-days">{t("platform.products.defaultBusinessDaysLabel")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.defaultBusinessDaysHint")}</p>
        <Input
          id="cert-business-days"
          inputMode="numeric"
          value={form.defaultBusinessDays}
          onChange={(e) => setForm((f) => ({ ...f, defaultBusinessDays: e.target.value }))}
          disabled={isSaving}
          className="transition-colors duration-150"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cert-sort">{t("platform.products.sortOrder")}</Label>
        <p className="text-xs text-muted-foreground">{t("platform.products.sortOrderHint")}</p>
        <Input
          id="cert-sort"
          inputMode="numeric"
          value={form.sortOrder}
          onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
          disabled={isSaving}
          className="transition-colors duration-150"
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="cert-price-active"
          checked={form.active}
          onCheckedChange={(c) => setForm((f) => ({ ...f, active: c === true }))}
          disabled={isSaving}
          className="transition-colors duration-150"
        />
        <Label htmlFor="cert-price-active" className="cursor-pointer font-normal">
          {t("platform.products.colActive")}
        </Label>
      </div>
    </div>
  );
}
